import { useState, useCallback, useEffect, useRef } from "react";
import { SandboxFilesystem } from "./Filesystem";
import { DirectoryItem_File, DirectoryItem, DirectoryItem_Directory } from "./fsTypes";
import type { ClientStatus } from "../SandboxContext";

export interface EditorState {
  currentPath: string | null;
  currentContents: string | null;
  serverContents: string | null;
  isDirty: boolean;
}

export interface UseFilesystemResult {
  filesystem: SandboxFilesystem | null;
  editor: EditorState;
  fileLookup: { [path: string]: DirectoryItem_File };
  error?: Error | string | null;
  beginEditingFile: (path: string) => Promise<void>;
  handleEditorChange: (newContents: string) => void;
  saveCurrentFile: () => Promise<boolean>;
  resetEditor: () => void;
  resetFilesystem: () => void;
}

/**
 * Hook to manage filesystem and editor state for the sandbox.
 * Handles file I/O operations, editor state, dirty state tracking, and filesystem initialization.
 */
export const useFilesystemInternal = (
  hasFilesystem: boolean,
  currentLanguage: string | null,
  clientStatus: ClientStatus
): UseFilesystemResult => {
  const errorRef = useRef<Error | string | null>(null);
  const isInitializingRef = useRef(false);
  const [filesystem, setFilesystem] = useState<SandboxFilesystem | null>(null);
  const [editor, setEditor] = useState<EditorState>({
    currentPath: null,
    currentContents: null,
    serverContents: null,
    isDirty: false,
  });

  // Map file paths to their directory items for constant-time lookups
  const [fileLookup, setFileLookup] = useState({} as { [path: string]: DirectoryItem_File });

  /**
   * Recursively populate fileLookup with all files in the directory tree
   */
  const populateFileLookup = useCallback(
    (items: DirectoryItem[], parentDirPath: string) => {
      const newLookup = { ...fileLookup };
      const populate = (items: DirectoryItem[], parentPath: string) => {
        for (const item of items) {
          const path = parentPath ? `${parentPath}/${item.name}` : item.name;
          const di = item as DirectoryItem_Directory;
          if (typeof di.directory !== "undefined") {
            populate(di.directory, path);
          } else {
            const fi = item as DirectoryItem_File;
            newLookup[path] = fi;
          }
        }
      };
      populate(items, parentDirPath);
      setFileLookup(newLookup);
    },
    [fileLookup]
  );

  /**
   * Begin editing a file by fetching its contents
   */
  const beginEditingFile = useCallback(
    async (path: string) => {
      if (!filesystem) {
        throw new Error("Filesystem is not initialized");
      }

      try {
        const contents = await filesystem.fetchFileContents(path);
        setEditor({
          currentPath: path,
          currentContents: contents,
          serverContents: contents,
          isDirty: false,
        });
      } catch (err) {
        console.error(`Failed to fetch file contents for ${path}:`, err);
        throw err;
      }
    },
    [filesystem]
  );

  /**
   * Handle editor content changes
   */
  const handleEditorChange = useCallback((newContents: string) => {
    setEditor(prev => ({
      ...prev,
      currentContents: newContents,
      isDirty: newContents !== prev.serverContents,
    }));
  }, []);

  /**
   * Save the current file
   */
  const saveCurrentFile = useCallback(async (): Promise<boolean> => {
    if (!filesystem) {
      console.error("Cannot save: Filesystem is not initialized");
      return false;
    }

    if (!editor.currentPath) {
      console.error("Cannot save: No file is currently being edited");
      return false;
    }

    if (editor.currentContents === null) {
      console.error("Cannot save: No editor contents available");
      return false;
    }

    try {
      const success = await filesystem.writeFileContents(
        editor.currentPath,
        editor.currentContents
      );
      if (success) {
        console.log(`Successfully saved ${editor.currentPath}`);
        // Update serverContents to match currentContents and mark as not dirty
        setEditor(prev => ({
          ...prev,
          serverContents: prev.currentContents,
          isDirty: false,
        }));
      }
      return success;
    } catch (err) {
      console.error(`Failed to save ${editor.currentPath}:`, err);
      return false;
    }
  }, [filesystem, editor.currentPath, editor.currentContents]);

  /**
   * Reset editor state
   */
  const resetEditor = useCallback(() => {
    setEditor({
      currentPath: null,
      currentContents: null,
      serverContents: null,
      isDirty: false,
    });
  }, []);

  /**
   * Reset entire filesystem state (filesystem, editor, and file lookup)
   */
  const resetFilesystem = useCallback(() => {
    console.log("Resetting filesystem and editor state");
    setFilesystem(null);
    setFileLookup({});
    resetEditor();
  }, [resetEditor]);

  /**
   * Initialize filesystem when currentLanguage and sandbox status are ready
   */
  useEffect(() => {
    const initFilesystem = async () => {
      if (!hasFilesystem || !currentLanguage || errorRef.current) {
        return;
      }

      try {
        const fs = new SandboxFilesystem();
        await fs.init(currentLanguage);
        setFilesystem(fs);

        // Fetch directory listing and populate file lookup
        setFileLookup({});
        const listing = await fs.fetchDirectoryListing();
        if (listing) {
          populateFileLookup(listing, "");
        }
      } catch (err) {
        console.error("Failed to initialize filesystem:", err);
        errorRef.current = err as Error | string;
        setFilesystem(null);
        setFileLookup({});
      } finally {
        isInitializingRef.current = false;
      }
    };

    // Only initialize if sandbox is running and filesystem is not yet set
    if (!isInitializingRef.current && currentLanguage && clientStatus === "ready" && !filesystem) {
      isInitializingRef.current = true;
      console.log("Initializing filesystem for language:", currentLanguage);
      initFilesystem();
    }
  }, [currentLanguage, filesystem, clientStatus, isInitializingRef, populateFileLookup]);

  return {
    filesystem,
    editor,
    fileLookup,
    error: errorRef.current,
    beginEditingFile,
    handleEditorChange,
    saveCurrentFile,
    resetEditor,
    resetFilesystem,
  };
};
